import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Plus, AlertTriangle, IndianRupee, TrendingUp, Wallet, Receipt, ArrowDownLeft, ArrowUpRight, Car, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { inr, dmy } from "@/lib/format";
import { PageHeader, Panel, StatusBadge, EmptyState } from "@/components/common";
import { MobileFab } from "@/components/MobileFab";

function Kpi({ label, value, sub, icon: Icon, tone = "text-foreground", testid, delay = 0 }) {
  return (
    <div className="rounded-xl bg-card border p-4 sm:p-5 shadow-sm min-w-0 hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 rise"
      style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="eyebrow leading-snug">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
      </div>
      <div className={`mt-2 sm:mt-3 font-heading text-xl sm:text-3xl font-bold tracking-tight num break-words ${tone}`} data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[11px] sm:text-xs text-muted-foreground mt-1.5 break-words">{sub}</div>}
    </div>
  );
}

const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading dashboard...</div>;

  const pTone = (v) => (v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600");
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Executive Overview" title="Dashboard" subtitle="Live view of billing, collections, costs and profit."
        actions={<Button asChild className="h-11 sm:h-10" data-testid="dashboard-new-duty-btn"><Link to="/duties/new"><Plus className="h-4 w-4 mr-1.5" /> New Duty</Link></Button>} />

      <div className="eyebrow mb-3">Today</div>
      <div className="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Kpi testid="kpi-today-billing" label="Today's Billing" value={inr(d.today.billing)} sub={`${d.today.duties} duties`} icon={IndianRupee} delay={0} />
        <Kpi testid="kpi-today-collections" label="Today's Collections" value={inr(d.today.collections)} sub="Invoice payments received" icon={ArrowDownLeft} delay={50} />
        <Kpi testid="kpi-today-expenses" label="Today's Expenses" value={inr(d.today.expenses)} sub="Duty costs & payouts" icon={ArrowUpRight} delay={100} />
        <Kpi testid="kpi-today-profit" label="Today's Profit" value={inr(d.today.profit)} tone={pTone(d.today.profit)} icon={TrendingUp} delay={150} />
      </div>

      <div className="eyebrow mb-3">This Month</div>
      <div className="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Kpi testid="kpi-month-billing" label="Monthly Billing" value={inr(d.month.billing)} sub={`${d.month.duties} duties · ${d.month.km.toLocaleString("en-IN")} km`} icon={Receipt} />
        <Kpi testid="kpi-month-profit" label="Monthly Profit" value={inr(d.month.profit)} tone={pTone(d.month.profit)} sub={`Market commission ${inr(d.month.market_commission)}`} icon={TrendingUp} />
        <Kpi testid="kpi-receivables" label="Outstanding Receivables" value={inr(d.receivables.total)} tone="text-amber-600 dark:text-amber-400"
          sub={`Invoiced ${inr(d.receivables.invoiced)} · Unbilled ${inr(d.receivables.unbilled)}`} icon={Wallet} />
        <Kpi testid="kpi-payables" label="Outstanding Payables" value={inr(d.payables)} tone="text-rose-600" sub="Due to market vehicle owners" icon={ArrowUpRight} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <Panel title="6-Month Billing vs Profit" className="xl:col-span-2" testid="trend-chart">
          <div className="h-60 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trend}>
                <defs>
                  <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} interval="preserveStartEnd" stroke="hsl(var(--muted-foreground))" />
                <YAxis width={46} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `₹${v >= 100000 ? `${+(v / 100000).toFixed(1)}L` : v >= 1000 ? `${+(v / 1000).toFixed(1)}k` : v}`} />
                <Tooltip {...tip} formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="billing" name="Billing" stroke="#2563EB" strokeWidth={2} fill="url(#gb)" />
                <Area type="monotone" dataKey="profit" name="Profit" stroke="#059669" strokeWidth={2} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Company Revenue · Month" testid="company-revenue">
          {d.company_revenue.length === 0 ? <EmptyState title="No duties this month" /> : (
            <div className="h-60 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.company_revenue} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={84} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip {...tip} formatter={(v) => inr(v)} />
                  <Bar dataKey="revenue" name="Revenue" fill="#D97706" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 pb-20 lg:pb-0">
        <Panel title="Own Vehicle Profitability · Month" testid="vehicle-profit">
          {d.vehicle_profit.length === 0 ? (
            <EmptyState icon={Car} title="No own vehicles" action={<Button asChild variant="outline" size="sm"><Link to="/vehicles">Add vehicles</Link></Button>} />
          ) : (
            <div className="space-y-3">
              {d.vehicle_profit.map((v) => (
                <div key={v.id} className="rounded-lg border p-4" data-testid={`vehicle-profit-${v.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-mono text-sm font-semibold">{v.vehicle_number}</div>
                      <div className="text-xs text-muted-foreground">{v.model} · {v.duties} duties · {v.km} km</div>
                    </div>
                    <div className={`font-heading font-bold num ${pTone(v.profit)}`}>{inr(v.profit)}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 num">Billing {inr(v.billing)} · Cost {inr(v.cost)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Document & Service Alerts" testid="alerts-panel">
          {d.alerts.length === 0 ? <EmptyState title="All clear" text="No vehicle documents expiring in the next 30 days." /> : (
            <div className="space-y-2">
              {d.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${a.overdue ? "border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900" : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900"}`} data-testid={`alert-${i}`}>
                  <AlertTriangle className={`h-4 w-4 ${a.overdue ? "text-rose-600" : "text-amber-600"}`} />
                  <div className="text-sm min-w-0">
                    <span className="font-semibold">{a.type}</span> · <span className="font-mono">{a.vehicle_number}</span>
                  </div>
                  <div className="ml-auto text-xs font-semibold whitespace-nowrap">{a.overdue ? "Expired " : "Due "}{dmy(a.date)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Recent Duties" action={<Link to="/duties" className="text-xs font-semibold text-blue-600 hover:underline">View all</Link>} testid="recent-duties">
          {d.recent_duties.length === 0 ? (
            <EmptyState icon={ClipboardList} title="No duties yet" action={<Button asChild size="sm"><Link to="/duties/new">Create first duty</Link></Button>} />
          ) : (
            <div className="divide-y">
              {d.recent_duties.map((x) => (
                <Link key={x.id} to={`/duties/${x.id}`} className="flex items-center gap-3 py-2.5 hover:bg-muted/50 -mx-2 px-2 rounded transition-colors" data-testid={`recent-duty-${x.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{x.company_name} <span className="font-mono text-xs text-muted-foreground">{x.duty_no}</span></div>
                    <div className="text-xs text-muted-foreground truncate">{dmy(x.duty_date)} · {x.guest_name || "-"}</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-sm font-semibold num">{inr(x.billing_total)}</div>
                    <StatusBadge value={x.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
      <MobileFab to="/duties/new" label="New Duty" testid="dashboard-fab-new-duty" />
    </div>
  );
}
